import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import styled from 'styled-components';

const ModalOverlay = styled(motion.div)`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  backdrop-filter: blur(5px);
  
  /* Hide any elements with higher z-index that might appear on top */
  & * {
    z-index: auto !important;
  }
`;

const ModalWrapper = styled(motion.div)`
  width: 1100px;
  max-width: 90%;
  height: 90vh;
  background: #ffffff;
  border-radius: 16px;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
  position: relative;
  z-index: 10000;
  
  /* Ensure wrapper doesn't clip content */
  & > * {
    position: relative;
    z-index: auto;
  }
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 2rem;
  flex-shrink: 0;
  background: #ffffff;
  border-bottom: 1px solid #e2e8f0;
`;

const ModalTitle = styled.h2`
  font-size: 1.8rem;
  font-weight: 700;
  color: var(--text-primary);
  background: var(--gradient-primary);
  // font-family: 'Manrope', sans-serif;
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  margin: 0;
`;

const CloseButton = styled.button`
  background: transparent;
  border: none;
  font-size: 1.5rem;
  cursor: pointer;
  color: var(--text-secondary);
  padding: 0;
  width: 2rem;
  height: 2rem;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: color 0.2s ease;

  &:hover {
    color: var(--text-primary);
  }

  &:active {
    transform: scale(0.95);
  }
`;

const ModalBody = styled.div`
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
  padding: 2rem;
  min-height: 0;
  
  /* Explicit visibility fixes */
  display: block !important;
  visibility: visible !important;
  opacity: 1 !important;
  color: #000 !important; /* Ensure text is dark */
  background-color: #ffffff !important; /* Ensure white background */
  position: relative;
  z-index: 10001;

  /* Custom scrollbar styling */
  &::-webkit-scrollbar {
    width: 8px;
  }

  &::-webkit-scrollbar-track {
    background: rgba(0, 102, 255, 0.05);
    border-radius: 4px;
  }

  &::-webkit-scrollbar-thumb {
    background: rgba(0, 102, 255, 0.2);
    border-radius: 4px;

    &:hover {
      background: rgba(0, 102, 255, 0.3);
    }
  }
  
  /* Ensure child elements are visible */
  & * {
    color: inherit;
    visibility: visible !important;
    opacity: 1 !important;
  }
`;

const ModalFooter = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 1rem;
  flex-shrink: 0;
  padding: 1.5rem 2rem;
  border-top: 1px solid rgba(0, 102, 255, 0.1);
  background: #ffffff;
`;

const Button = styled.button`
  padding: 0.75rem 1.5rem;
  border-radius: 8px;
  border: none;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;

  &:active {
    transform: scale(0.98);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const CancelButton = styled(Button)`
  background: transparent;
  color: var(--text-secondary);
  border: 1px solid rgba(0, 102, 255, 0.2);

  &:hover:not(:disabled) {
    background: rgba(0, 102, 255, 0.05);
    border-color: rgba(0, 102, 255, 0.4);
    color: var(--text-primary);
  }
`;

const PrimaryButton = styled(Button)`
  background: var(--gradient-primary);
  color: white;

  &:hover:not(:disabled) {
    box-shadow: 0 4px 12px rgba(0, 102, 255, 0.3);
    transform: translateY(-2px);
  }
`;

const SecondaryButton = styled(Button)`
  background: transparent;
  color: var(--text-primary);
  border: 1px solid var(--text-secondary);

  &:hover:not(:disabled) {
    background: rgba(0, 102, 255, 0.05);
    border-color: var(--text-primary);
  }
`;

const GenericModal = ({
  isOpen,
  onClose,
  title,
  children,
  footer,
  size = 'md', // 'sm', 'md', 'lg'
  showDefaultFooter = true,
  onConfirm,
  confirmText = 'Confirm',
  cancelText = 'Cancel',
  isLoading = false,
  className,
}) => {
  // Size mappings
  const sizeConfig = {
    xsm: { width: '500px', height: '50vh' },
    sm: { width: '600px', height: '60vh' },
    md: { width: '1100px', height: '90vh' },
    lg: { width: '1400px', height: '95vh' },
    xlg: {width: '1600px', height: '95vh'}
  };

  const currentSize = sizeConfig[size];

  const modalVariants = {
    hidden: { scale: 0.9, opacity: 0 },
    visible: { scale: 1, opacity: 1 },
    exit: { scale: 0.9, opacity: 0 },
  };

  const overlayVariants = {
    hidden: { opacity: 0 },
    visible: { opacity: 1 },
    exit: { opacity: 0 },
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <ModalOverlay
          variants={overlayVariants}
          initial="hidden"
          animate="visible"
          exit="exit"
          onClick={onClose}
        >
          <ModalWrapper
            variants={modalVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
            onClick={(e) => e.stopPropagation()}
            style={{
              width: currentSize.width,
              height: currentSize.height,
            }}
            className={className}
          >
            {/* Header */}
            <ModalHeader>
              <ModalTitle>{title}</ModalTitle>
              <CloseButton onClick={onClose} aria-label="Close modal">
                ×
              </CloseButton>
            </ModalHeader>

            {/* Body */}
            <ModalBody>{children}</ModalBody>

            {/* Footer */}
            {showDefaultFooter ? (
              <ModalFooter>
                {footer ? (
                  footer
                ) : (
                  <>
                    <CancelButton onClick={onClose} disabled={isLoading}>
                      {cancelText}
                    </CancelButton>
                    {onConfirm && (
                      <PrimaryButton
                        onClick={onConfirm}
                        disabled={isLoading}
                      >
                        {isLoading ? 'Loading...' : confirmText}
                      </PrimaryButton>
                    )}
                  </>
                )}
              </ModalFooter>
            ) : null}
          </ModalWrapper>
        </ModalOverlay>
      )}
    </AnimatePresence>
  );
};

export default GenericModal;